import React from "react";
import cx from "classnames";
import { appstr } from "@coreapp/utils/appstr";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { Menu, OverflowText, Switch, Text } from "@theorgchartdev/storybook";

/**
 * SwitchWrapper - Handles the toggle interaction for field options
 */
export const SwitchWrapper = ({ checked, onChange, option, disabled, tooltipProps }) => {
  const handleSwitchChange = event => {
    onChange(option, checked);
    event.stopPropagation();
  };

  return (
    <Switch
      checked={checked}
      onChange={handleSwitchChange}
      disabled={disabled}
      tooltipProps={tooltipProps}
    />
  );
};

/**
 * Helper to create a composite field identifier
 */
export const createCompositeFieldId = (fieldId, className) => {
  return `${className}::${fieldId}`;
};

/**
 * MultiClassFieldToggleMenu - A specialized field toggle menu for selecting fields from multiple classes
 * This component is specifically designed to handle fields from different classes (Box, Position)
 * without worrying about backward compatibility.
 * @param localizationMap - Object containing localization strings for field labels grouped by class and field id. Record<objClassName, Record<fieldId, string>>
 */
export const MultiClassFieldToggleMenu = ({
  // Selected field IDs in the format "ClassName::FieldId"
  selectedFieldIds,
  // Function to call when a field is toggled
  onChange,
  // Boolean to control size of menu items
  small = false,
  // Base prefix for data-testid attributes
  dataTestIdPrefix,
  // Array of field definitions to display
  fieldDefs = [],
  // Trigger element for the menu
  trigger,
  // Optionally use field names for checked state
  useFieldNames = false,
  // Disabled field IDs
  disabledFieldIds,
  // Function to get hover tip content for disabled fields
  getHoverTipContent,
  localizationMap = undefined,
  // Additional props for the Menu component,
  ...menuProps
}) => {
  // Build class-based sections of field options
  const buildFieldOptions = () => {
    const options = [];
    const baseClass = "ocn-field-toggle-option";
    const textSize = small ? "xsmall" : "small";

    // Group field definitions by class
    const fieldsByClass = fieldDefs.reduce((acc, fieldDef) => {
      const className = fieldDef.GetFieldClassName();
      if (!acc[className]) {
        acc[className] = [];
      }
      acc[className].push(fieldDef);
      return acc;
    }, {});

    // Process each class group
    Object.entries(fieldsByClass).forEach(([className, classDefs]) => {
      // Add class header
      options.push({
        label: className,
        value: `${className}-header`,
        clickHandler: () => {},
        content: (
          <Text size="small" className={`${baseClass}-label`} modifier="medium">
            {className === "Box" ? appstr("Employee fields") : appstr("Position fields")}
          </Text>
        )
      });

      // Sort fields by name
      const sortedFields = [...classDefs].sort((a, b) => {
        const nameA = a.GetFieldName();
        const nameB = b.GetFieldName();
        return nameA.localeCompare(nameB);
      });

      // Add field options
      sortedFields.forEach(fieldDef => {
        const fieldId = fieldDef.GetFieldID();
        const fieldName = fieldDef.GetFieldName();
        const label = localizationMap?.[className]?.[fieldId] ?? fieldName;
        const compositeId = useFieldNames
          ? createCompositeFieldId(fieldName, className)
          : createCompositeFieldId(fieldId, className);
        let checked = selectedFieldIds?.includes(compositeId);

        let checkedClass = checked && `${baseClass}-label--checked`;
        const isDisabled = disabledFieldIds?.includes(compositeId);

        const fieldOption = {
          content: (
            <span className={baseClass}>
              <span className={cx(`${baseClass}-label`, checkedClass)}>
                <OverflowText size={textSize}>{label}</OverflowText>
              </span>
              <span className={`${baseClass}-switch`}>
                <SwitchWrapper
                  id={`multiclass-field-toggle-${className}-${fieldId}`}
                  checked={checked}
                  disabled={isDisabled}
                  option={{
                    value: compositeId
                  }}
                  tooltipProps={
                    isDisabled && getHoverTipContent
                      ? {
                          content: getHoverTipContent(fieldName),
                          position: "right-end"
                        }
                      : undefined
                  }
                  onChange={onChange}
                />
              </span>
            </span>
          ),
          clickHandler: isDisabled ? undefined : onChange,
          value: compositeId,
          label,
          disabled: isDisabled,
          className: isDisabled ? "ocn-field-toggle-option--disabled" : "",
          fieldDef
        };

        if (dataTestIdPrefix) {
          fieldOption.dataTestId = `${dataTestIdPrefix}${className}-${fieldName}`;
        }

        options.push(fieldOption);
      });
    });

    return options;
  };

  // Get all menu options
  const menuOptions = buildFieldOptions();

  if (!menuOptions || menuOptions.length === 0) {
    return null;
  }

  // Configure list props with search capability
  const listWidth = menuProps?.listProps?.listWidth ?? "large";

  if (menuProps?.listProps?.enableSearch !== false) {
    menuProps = {
      ...menuProps,
      listProps: {
        ...menuProps?.listProps,
        ...FieldSelectListProps({
          customPlaceholder: menuProps?.listProps?.placeholder,
          customNoResultsText: menuProps?.listProps?.noResultsText
        }),
        listWidth: listWidth
      }
    };
  }

  return (
    <Menu options={menuOptions} stayOpenOnOptionSelect={true} trigger={trigger} {...menuProps} />
  );
};

export default MultiClassFieldToggleMenu;
